import { Controller } from "@hotwired/stimulus"

// Replica PlacesSearch.tsx del frontend.
// Usa Google Places Autocomplete API para buscar direcciones con debounce 300ms.
// Al seleccionar sugerencia, extrae lat/lng, country, city, locality via getDetails.
const API_KEY = "AIzaSyDH5JjS5uBbnTzZTEMGQQvdM4dUMANncNA"

export default class extends Controller {
  static targets = [
    "searchInput", "suggestions",
    "line1", "locality", "countryId", "city", "lat", "lng", "extraData",
    "addressFields", "mapContainer"
  ]
  static values = { hasAddress: Boolean }

  connect() {
    this.debounceTimer = null
    this.autocompleteService = null
    this.placesService = null
    this.loadGoogleMaps().then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService()
      this.placesService = new google.maps.places.PlacesService(
        document.createElement("div")
      )
    })

    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleClickOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  loadGoogleMaps() {
    if (window.google?.maps?.places) return Promise.resolve()

    return new Promise((resolve) => {
      if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
        const check = setInterval(() => {
          if (window.google?.maps?.places) { clearInterval(check); resolve() }
        }, 50)
        return
      }
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`
      script.async = true
      script.onload = () => {
        const check = setInterval(() => {
          if (window.google?.maps?.places) { clearInterval(check); resolve() }
        }, 50)
      }
      document.head.appendChild(script)
    })
  }

  onInput() {
    const query = this.searchInputTarget.value.trim()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (query.length < 3) {
      this.hideSuggestions()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }

  fetchSuggestions(query) {
    if (!this.autocompleteService) return

    this.autocompleteService.getPlacePredictions(
      { input: query },
      (predictions, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          this.hideSuggestions()
          return
        }
        this.renderSuggestions(predictions)
      }
    )
  }

  renderSuggestions(predictions) {
    const container = this.suggestionsTarget
    container.innerHTML = ""

    const list = document.createElement("div")
    list.className = "bg-white rounded-[8px] shadow-sm border border-[#eaeaea] overflow-hidden"

    predictions.forEach((prediction) => {
      const item = document.createElement("div")
      item.className = "px-[12px] py-[10px] cursor-pointer hover:bg-[#f8f8f8] text-[14px]"
      item.innerHTML = `<strong>${prediction.structured_formatting.main_text}</strong> <span class="text-[#888] text-[13px]">${prediction.structured_formatting.secondary_text || ""}</span>`
      item.addEventListener("click", () => this.selectSuggestion(prediction))
      list.appendChild(item)
    })

    container.appendChild(list)
    container.classList.remove("hidden")
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add("hidden")
      this.suggestionsTarget.innerHTML = ""
    }
  }

  selectSuggestion(prediction) {
    this.searchInputTarget.value = prediction.description
    this.hideSuggestions()

    this.placesService.getDetails(
      { placeId: prediction.place_id, fields: ["address_components", "geometry", "url", "place_id"] },
      (result, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) return
        this.populateFields(prediction.description, result)
      }
    )
  }

  populateFields(description, result) {
    const components = result.address_components || []

    const getComponent = (type, nameType = "long_name") => {
      const comp = components.find(c => c.types.includes(type))
      return comp ? comp[nameType] : ""
    }

    this.line1Target.value = description
    this.localityTarget.value = getComponent("locality")
    this.countryIdTarget.value = getComponent("country", "short_name")
    this.cityTarget.value = getComponent("administrative_area_level_2")

    const lat = result.geometry?.location?.lat()
    const lng = result.geometry?.location?.lng()
    this.latTarget.value = lat || ""
    this.lngTarget.value = lng || ""

    const extraData = {}
    if (result.url) extraData.url = result.url
    if (result.place_id) extraData.place_id = result.place_id
    this.extraDataTarget.value = Object.keys(extraData).length ? JSON.stringify(extraData) : ""

    // Mostrar campos de direccion
    this.addressFieldsTarget.classList.remove("hidden")

    // Mostrar y actualizar mapa
    if (lat && lng) {
      this.mapContainerTarget.classList.remove("hidden")
      const mapEl = this.mapContainerTarget.querySelector("[data-controller*='manage--google-map']")
      if (mapEl) {
        mapEl.setAttribute("data-manage--google-map-lat-value", lat)
        mapEl.setAttribute("data-manage--google-map-lng-value", lng)
        // Reconnect controller para reinicializar mapa
        const controller = this.application.getControllerForElementAndIdentifier(mapEl, "manage--google-map")
        if (controller) {
          controller.latValue = lat
          controller.lngValue = lng
          controller.connect()
        }
      }
    }
  }
}
